import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createOrganizationInvitesQueryKey } from '@/utils/queryKeys';
import type {
  RevokeOrganizationInviteMutation,
  RevokeOrganizationInviteMutationVariables,
} from './useRevokeOrganizationInvite.generated';

const query = gql`
  mutation RevokeOrganizationInvite($input: RevokeOrganizationInviteInput!) {
    revokeOrganizationInvite(input: $input)
  }
`;

export const useRevokeOrganizationInvite = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    RevokeOrganizationInviteMutation,
    QueryError,
    RevokeOrganizationInviteMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        RevokeOrganizationInviteMutation,
        RevokeOrganizationInviteMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createOrganizationInvitesQueryKey(organizationId),
      });
    },
  });

  const revokeInvite = (
    inviteId: string,
    options?: MutateOptions<
      RevokeOrganizationInviteMutation,
      QueryError,
      RevokeOrganizationInviteMutationVariables
    >
  ) => mutate({ input: { organizationId, inviteId } }, options);

  return {
    revokeInvite,
    isPending,
  };
};
